import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/nb';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import { fetchSS13Export, fetchSS13OrderDayReport } from '../../felles/APICalls_SS13';
import Utils from '../../felles/Utils';
import FirmNavnId from '../../models/FirmNavnId';
import { SS13OrderDayReport, SS13OrderDayRow } from '../../models/SS13OrderDayReport';
import * as actions from '../../store/actions/index';
import { AppState } from '../../store/rootReducer';
import StorePicker, { storeIds, storeNames } from '../SS10/components/StorePicker';
import MonthPicker from '../SS12/MonthPicker';

// ─── Formatting (user story: whole kroner with a space as thousands separator, "+4,2 %") ─────────────

const amountFormat = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 });
const percentFormat = new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const formatAmount = (v: number | null | undefined) => (v == null ? '–' : amountFormat.format(Math.round(v)));
const formatSignedAmount = (v: number | null | undefined) => (v == null ? '–' : (Math.round(v) > 0 ? '+' : '') + formatAmount(v));
const formatSignedPercent = (v: number | null | undefined) => (v == null ? '–' : (v > 0 ? '+' : '') + percentFormat.format(v) + ' %');
const formatPercent = (v: number | null | undefined) => (v == null ? '–' : percentFormat.format(v) + ' %');
const isNegative = (v: number | null | undefined) => v != null && Math.round(v * 10) < 0;
const formatDate = (date: string) => dayjs(date).format('DD.MM.YYYY');

// ─── Styles (compact, like NO04 / NR01 / SS12) ─────────────────────────────────────────────────

const CARD_SX = { p: 1.5, borderRadius: 2 } as const;
// The store list the same compact height as NO04's fields (34px, 0.85rem); StorePicker itself is shared with SS10.
const COMPACT_FIELD_SX = { '& .MuiInputBase-root': { minHeight: 34, py: '0 !important', fontSize: '0.85rem' } } as const;
const TABLE_SX = {
  '& .MuiTableCell-root': { py: 0.6, px: 1, fontSize: '0.82rem', whiteSpace: 'nowrap' },
  '& .MuiTableCell-head': { fontWeight: 600, color: 'text.secondary', backgroundColor: 'background.paper' },
} as const;
const NEGATIVE_RED = { color: '#c62828' } as const;
const MUTED = { color: 'text.secondary' } as const;
const EXPORT_BUTTON_SX = { height: 30, fontSize: '0.8rem', textTransform: 'none' } as const;
const KEY_FIGURE_LABEL_SX = { fontSize: '0.72rem', fontWeight: 700, letterSpacing: 1, color: 'text.secondary' } as const;

// ─── Columns (the keys are the API's export column keys) ───────────────────────────────────────

interface Column {
  key: string;
  label: string;
  /** Text-like columns are left-aligned. */
  left?: boolean;
  /** Filled by the second call; "…" until then. */
  accumulated?: boolean;
  render: (row: SS13OrderDayRow) => ReactNode;
  negative?: (row: SS13OrderDayRow) => boolean;
}

const DateWithWeekday = ({ date, weekday }: { date: string | null; weekday: string }) =>
  date ? <>{formatDate(date)}<Box component="span" sx={{ ml: 0.75, ...MUTED }}>{weekday}</Box></> : null;

const COLUMNS: Column[] = [
  { key: 'salesCount', label: 'Antall salgsordre', render: (r) => r.salesCount },
  { key: 'salesAmount', label: 'Salg på ordre', render: (r) => formatAmount(r.salesAmount) },
  { key: 'creditCount', label: 'Antall kredit', render: (r) => r.creditCount },
  { key: 'creditAmount', label: 'Kredit', render: (r) => formatAmount(r.creditAmount), negative: (r) => isNegative(r.creditAmount) },
  { key: 'net', label: 'Netto samlet', render: (r) => formatAmount(r.net), negative: (r) => isNegative(r.net) },
  { key: 'lastYearDate', label: 'Samme ukedag i fjor', left: true, render: (r) => <DateWithWeekday date={r.lastYearDate} weekday={r.lastYearWeekday} /> },
  { key: 'netLastYear', label: 'Netto i fjor', render: (r) => formatAmount(r.netLastYear), negative: (r) => isNegative(r.netLastYear) },
  { key: 'difference', label: 'Diff.', render: (r) => formatSignedAmount(r.difference), negative: (r) => isNegative(r.difference) },
  { key: 'changePercent', label: '%', render: (r) => formatSignedPercent(r.changePercent), negative: (r) => isNegative(r.changePercent) },
  { key: 'accumulatedThisYear', label: 'Akkumulert i år', accumulated: true, render: (r) => formatAmount(r.accumulatedThisYear), negative: (r) => isNegative(r.accumulatedThisYear) },
  { key: 'accumulatedLastYear', label: 'Akkumulert i fjor', accumulated: true, render: (r) => formatAmount(r.accumulatedLastYear), negative: (r) => isNegative(r.accumulatedLastYear) },
];

// This year | last year: a vertical line (the same as above the sum row) before the first last-year
// column shown - "Samme ukedag i fjor", or "Netto i fjor" when that is hidden. Same rule as
// OrderDayColumns.LastYearStart (Excel / PDF).
const lastYearStart = (columns: Column[]) => columns.find((c) => c.key === 'lastYearDate' || c.key === 'netLastYear')?.key;
const LAST_YEAR_LINE_SX = { borderLeft: '2px solid', borderLeftColor: 'divider', pl: 1.5 } as const;
const lineBefore = (column: Column, lineKey: string | undefined) => (column.key === lineKey ? LAST_YEAR_LINE_SX : {});

// The column choice is remembered in this browser ("Valget huskes til neste gang"). Hidden columns are
// stored, so a column added later shows up by default.
const HIDDEN_COLUMNS_KEY = 'SS13.hiddenColumns';
const readHiddenColumns = (): string[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_KEY) ?? '[]');
    return Array.isArray(stored) ? stored.filter((k) => typeof k === 'string') : [];
  } catch {
    return [];
  }
};
const saveHiddenColumns = (hidden: string[]) => {
  try {
    localStorage.setItem(HIDDEN_COLUMNS_KEY, JSON.stringify(hidden));
  } catch {
    // No storage (private window, blocked site data): the choice just isn't remembered.
  }
};

const EXPLANATION = 'Tallene viser registrerte ordrer per ordredato og er ikke fakturerte tall. Tidligere dager kan endre seg hvis ordrer blir korrigert eller utgårmeldt.';


export default function SS13OrderDays() {
  const dispatch = useDispatch();
  const userJWT = useSelector((state) => (state as AppState).auth.userJWT);
  const firmListFromAdmin: FirmNavnId[] = useSelector((state) => (state as AppState).userAdmin.firmListFromAdmin);
  const isFetchingAdminFirmData: boolean = useSelector((state) => (state as AppState).userAdmin.isFetchingAdminFirmData);

  // The logged-in company is the main company (hovedfirma) and the default selection, as in SS10/SS12.
  const mainCompany = new FirmNavnId(Number(Utils.T_companyNr), userJWT?.CompanyName ?? '', 0);

  const [stores, setStores] = useState<FirmNavnId[]>([mainCompany]);
  const [month, setMonth] = useState<Dayjs>(dayjs().startOf('month'));
  const [perStoreChecked, setPerStoreChecked] = useState(false);
  const [report, setReport] = useState<SS13OrderDayReport | null>(null);
  const [loading, setLoading] = useState(false);
  /** The accumulated columns come in a second, slower call. */
  const [accumulatedState, setAccumulatedState] = useState<'loading' | 'done' | 'failed'>('loading');
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(readHiddenColumns);
  const [columnMenu, setColumnMenu] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!isFetchingAdminFirmData && (firmListFromAdmin ?? []).length === 0) dispatch(actions.firmListFromDBGet());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monthKey = month.format('YYYYMM');
  const storeKey = storeIds(stores);
  // "Fordel på butikk" is only there with more than one store.
  const canSplitPerStore = stores.length > 1;
  const perStore = perStoreChecked && canSplitPerStore;
  const visibleColumns = COLUMNS.filter((c) => !hiddenColumns.includes(c.key));

  const toggleColumn = (key: string) => {
    const hidden = hiddenColumns.includes(key) ? hiddenColumns.filter((k) => k !== key) : [...hiddenColumns, key];
    setHiddenColumns(hidden);
    saveHiddenColumns(hidden);
  };

  // Month, store or "Fordel på butikk" changed: read the orders again, as they are now. First the days
  // of the month (fast), then once more with the accumulated columns, which read every order since
  // 1 January last year (slow) - the table is usable while they load.
  useEffect(() => {
    if (!storeKey) {
      setReport(null);
      return;
    }
    let active = true;   // ignore the answer to an older request
    setLoading(true);
    setAccumulatedState('loading');
    setError('');
    fetchSS13OrderDayReport(monthKey, storeKey, perStore, false)
      .then((res) => {
        if (!active) return;
        setLoading(false);
        if (res.data.message && res.data.message !== 'OK') {
          setError(res.data.message);
          setReport(res.data);
          setAccumulatedState('failed');
          return;
        }
        setReport(res.data);
        return fetchSS13OrderDayReport(monthKey, storeKey, perStore, true)
          .then((full) => {
            if (!active) return;
            setReport(full.data);
            setAccumulatedState('done');
          })
          .catch(() => {
            if (active) setAccumulatedState('failed');
          });
      })
      .catch(() => {
        if (!active) return;
        setLoading(false);
        setAccumulatedState('failed');
        setError('Kunne ikke hente ordrene. Prøv igjen.');
      });
    return () => {
      active = false;
    };
  }, [monthKey, storeKey, perStore]);

  // Excel / PDF of the day table as the page shows it (the loaded report is sent along).
  const downloadExport = (format: 'excel' | 'pdf') => {
    if (!report) return;
    setExporting(format);
    setError('');
    fetchSS13Export(format, {
      report,
      storeNames: storeNames(stores),
      columns: visibleColumns.map((c) => c.key),
    })
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Salg-pr-dag-${monthKey}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      })
      .catch(() => setError('Kunne ikke lage filen. Prøv igjen.'))
      .finally(() => setExporting(null));
  };

  const monthName = month.locale('nb').format('MMMM YYYY');
  // While a new month loads the old figures stay on screen, so their titles keep the old month.
  const reportMonthName = report ? dayjs(`${report.month.slice(0, 4)}-${report.month.slice(4)}-01`).locale('nb').format('MMMM YYYY') : monthName;
  const storeName = storeNames(stores);
  const columnCount = 1 + (report?.perStore ? 1 : 0) + visibleColumns.length;
  const totals = report?.totals;
  const pendingText = report?.hasAccumulated ? null : accumulatedState === 'failed' ? '–' : '…';

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0 }}>
      <Box>
        <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.5, letterSpacing: 1 }}>
          Rapporter salg
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
          Salg pr. dag
        </Typography>
      </Box>

      {/* Filter: store(s) and one month. */}
      <Paper variant="outlined" sx={{ ...CARD_SX, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '0 1 380px', minWidth: 260, ...COMPACT_FIELD_SX }}>
          <Typography variant="caption" color="text.secondary">Butikk liste</Typography>
          <StorePicker compact mainCompany={mainCompany} companies={firmListFromAdmin ?? []} value={stores} onChange={setStores} />
        </Box>
        <Box sx={{ flex: '0 0 180px' }}>
          <Typography variant="caption" color="text.secondary">Måned</Typography>
          <MonthPicker value={month} onChange={setMonth} maxDate={dayjs()} />
        </Box>
      </Paper>

      {loading && (
        <Box role="status" aria-live="polite">
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Henter ordrer for {monthName}…
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {!storeKey && <Alert severity="info">Velg minst én butikk.</Alert>}

      {report && storeKey && totals && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0, opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }} aria-busy={loading}>
          {/* Key figures for the month so far */}
          <Paper variant="outlined" sx={{ ...CARD_SX, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <KeyFigure label="Netto samlet" value={formatAmount(totals.net) + ' kr'} negative={isNegative(totals.net)}
              detail={`${totals.salesDays} salgsdager`} />
            <KeyFigure label="Salg på ordre" value={formatAmount(totals.salesAmount) + ' kr'}
              detail={`${totals.salesCount} salgsordre`} />
            <KeyFigure label="Kredit" value={formatAmount(totals.creditAmount) + ' kr'} negative
              detail={`${totals.creditCount} kreditnotaer · ${formatPercent(totals.creditPercent)} av salg`} />
          </Paper>

          {/* Day table */}
          <Paper variant="outlined" sx={{ ...CARD_SX, p: 0, flex: '1 0 320px', minHeight: 320, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', px: 1.5, py: 1 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                Salg pr. ordredato · {reportMonthName} · {storeName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">Alle ordre summert pr. ordredato</Typography>
                <Tooltip title={canSplitPerStore ? '' : 'Velg mer enn én butikk for å fordele på butikk'}>
                  <FormControlLabel
                    sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                    disabled={!canSplitPerStore}
                    control={<Checkbox size="small" checked={perStore} onChange={(e) => setPerStoreChecked(e.target.checked)} />}
                    label="Fordel på butikk"
                  />
                </Tooltip>
                <Button variant="outlined" size="small" startIcon={<ViewColumnOutlinedIcon fontSize="small" />} sx={EXPORT_BUTTON_SX}
                  onClick={(e) => setColumnMenu(e.currentTarget)} aria-haspopup="menu">
                  {hiddenColumns.length === 0 ? 'Alle kolonner' : `${visibleColumns.length + 1} av ${COLUMNS.length + 1} kolonner`}
                </Button>
                <Button variant="outlined" size="small" startIcon={<GridOnOutlinedIcon fontSize="small" />} sx={EXPORT_BUTTON_SX}
                  disabled={exporting !== null || loading} onClick={() => downloadExport('excel')}>
                  {exporting === 'excel' ? 'Lager…' : 'Excel'}
                </Button>
                <Button variant="outlined" size="small" startIcon={<PrintOutlinedIcon fontSize="small" />} sx={EXPORT_BUTTON_SX}
                  disabled={exporting !== null || loading} onClick={() => downloadExport('pdf')}>
                  {exporting === 'pdf' ? 'Lager…' : 'PDF'}
                </Button>
              </Box>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pb: 1 }}>
              {EXPLANATION}
              {!report.hasAccumulated && accumulatedState === 'loading' && !loading && ' · Henter akkumulerte tall fra 1. januar…'}
              {!report.hasAccumulated && accumulatedState === 'failed' && (
                <Box component="span" sx={NEGATIVE_RED}> · Akkumulerte tall kunne ikke hentes.</Box>
              )}
            </Typography>

            {/* Scrolls both ways inside the card; the headings stay on top (also on a tablet). */}
            <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table stickyHeader size="small" sx={{ ...TABLE_SX, minWidth: 960 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Ordredato</TableCell>
                    {report.perStore && <TableCell>Butikk</TableCell>}
                    {visibleColumns.map((c) => (
                      <TableCell key={c.key} align={c.left ? 'left' : 'right'} sx={lineBefore(c, lastYearStart(visibleColumns))}>{c.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.days.map((row, i) => (
                    <Fragment key={`${row.date}-${row.storeId ?? ''}`}>
                      {(i === 0 || report.days[i - 1].week !== row.week) && (
                        <TableRow>
                          <TableCell colSpan={columnCount} sx={{ fontWeight: 700, fontSize: '0.72rem !important', letterSpacing: 1, color: 'text.secondary', backgroundColor: 'action.hover' }}>
                            UKE {row.week}
                          </TableCell>
                        </TableRow>
                      )}
                      <DayRow row={row} columns={visibleColumns} perStore={report.perStore} pending={pendingText} />
                    </Fragment>
                  ))}
                  {report.days.length > 0 && <DayRow row={report.sum} columns={visibleColumns} perStore={report.perStore} pending={pendingText} isSum />}
                  {report.days.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columnCount} sx={{ textAlign: 'center', color: 'text.secondary', py: 3 }}>
                        Ingen ordrer i {reportMonthName}.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderTopColor: 'divider' }}>
              {totals.salesDays} salgsdager · netto i NOK, eks. mva. · kredit vises som negative beløp · akkumulert fra 1. januar
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Column picker: Dato can't be hidden. */}
      <Menu anchorEl={columnMenu} open={columnMenu !== null} onClose={() => setColumnMenu(null)}>
        <MenuItem dense disabled>
          <Checkbox size="small" checked disabled sx={{ p: 0.5, mr: 1 }} />
          <ListItemText primary="Ordredato" slotProps={{ primary: { fontSize: '0.85rem' } }} />
        </MenuItem>
        {COLUMNS.map((c) => (
          <MenuItem key={c.key} dense onClick={() => toggleColumn(c.key)}>
            <Checkbox size="small" checked={!hiddenColumns.includes(c.key)} sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primary={c.label} slotProps={{ primary: { fontSize: '0.85rem' } }} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

function KeyFigure({ label, value, detail, negative = false }: { label: string; value: string; detail: string; negative?: boolean }) {
  return (
    <Box>
      <Typography sx={KEY_FIGURE_LABEL_SX}>{label.toUpperCase()}</Typography>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.3, ...(negative ? NEGATIVE_RED : {}) }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{detail}</Typography>
    </Box>
  );
}

function DayRow({ row, columns, perStore, pending, isSum = false }: {
  row: SS13OrderDayRow; columns: Column[]; perStore: boolean;
  /** What the accumulated columns show while they aren't there: "…" while loading, "–" if they failed. */
  pending: string | null;
  isSum?: boolean;
}) {
  const sumSx = isSum ? { fontWeight: 700, borderTop: '2px solid', borderTopColor: 'divider' } : {};
  const lineKey = lastYearStart(columns);
  return (
    <TableRow hover={!isSum}>
      <TableCell sx={sumSx}>{isSum ? row.label : <DateWithWeekday date={row.date} weekday={row.weekday} />}</TableCell>
      {perStore && <TableCell sx={sumSx}>{row.storeId ? `${row.storeId} ${row.storeName ?? ''}` : ''}</TableCell>}
      {columns.map((c) => (c.accumulated && pending !== null ? (
        <TableCell key={c.key} align="right" sx={{ ...sumSx, ...MUTED, ...lineBefore(c, lineKey) }}>{pending}</TableCell>
      ) : (
        <TableCell key={c.key} align={c.left ? 'left' : 'right'} sx={{ ...sumSx, ...(c.negative?.(row) ? NEGATIVE_RED : {}), ...lineBefore(c, lineKey) }}>
          {c.render(row)}
        </TableCell>
      )))}
    </TableRow>
  );
}
